import fromExponential from 'from-exponential';

import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import { unitValueToFormattedUnitLabels } from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Formats a very small number to show 2 non-zero digits (or 1 if only 1 exists).
 * For values with > 5 decimals, preserves the first 2 non-zero digits to avoid truncation to 0.
 * Returns a decimal string to prevent scientific notation display.
 *
 * @param value - The numeric value to format
 * @returns The formatted value as a decimal string with 2 non-zero digits preserved (or 1 if only 1 exists)
 */
const formatSmallPidValue = (value: number): string => {
  // Convert to string with full precision to preserve all digits
  const valueStr = fromExponential(value).toString();
  const [integerPart, decimalPart = ''] = valueStr.split('.');

  // Find the first non-zero digit position in the decimal part
  let firstNonZeroIndex = -1;
  let nonZeroCount = 0;
  let lastNonZeroIndex = -1;

  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] !== '0') {
      if (firstNonZeroIndex === -1) {
        firstNonZeroIndex = i;
      }
      nonZeroCount++;
      lastNonZeroIndex = i;
      if (nonZeroCount === 2) {
        break;
      }
    }
  }

  // If we found at least 1 non-zero digit, truncate after the last one we found (up to 2)
  if (firstNonZeroIndex !== -1 && nonZeroCount >= 1) {
    const truncatedDecimal = decimalPart.substring(0, lastNonZeroIndex + 1);
    return `${integerPart}.${truncatedDecimal}`;
  }

  // Fallback to original value as decimal string if we can't find any non-zero digits
  return valueStr;
};

/**
 * Formatter for PID (Proportional-Integral-Derivative) controller values.
 * Limits decimal precision to a maximum of 6 decimal places for readability.
 * For very small values (> 5 decimals), preserves 2 non-zero digits to avoid truncation to 0.
 * Handles exponential notation by converting it to standard decimal format.
 *
 * @param {UnitValue} unitValue - The PID value and units to format
 * @param {any} unitValue.value - The numeric PID value (can be string or number)
 * @param {string} [unitValue.units] - The current units of the value
 * @param {string} [displayUnits] - Optional target units to transform to before formatting
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted numeric value (max 6 decimal places, or 2 non-zero digits for very small values)
 *   - unitsLabel: The unit string
 *   - valueUnitsLabel: Combined value and units with proper spacing
 *
 * @example
 * formatPid({ value: 0.12345678, units: '' })
 * // Returns: { valueLabel: '0.123457', unitsLabel: '', valueUnitsLabel: '0.123457' }
 *
 * @example
 * formatPid({ value: 0.000000123, units: 'gain' })
 * // Returns: { valueLabel: '0.00000012', unitsLabel: 'gain', valueUnitsLabel: '0.00000012 gain' }
 *
 * @example
 * formatPid({ value: '3.141592653589793', units: '' })
 * // Returns: { valueLabel: '3.141593', unitsLabel: '', valueUnitsLabel: '3.141593' }
 */
export const formatPid = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  const transformed = transformUnits(unitValue, displayUnits);

  let formattedValue = transformed.value;

  // if value is a number and have decimals
  if (!isNaN(Number(formattedValue)) && !Number.isInteger(Number(formattedValue))) {
    formattedValue = Number(formattedValue);

    const numDecimals = String(fromExponential(formattedValue)).split('.')[1]?.length || 0;

    if (numDecimals > 5) {
      // For very small values with > 5 decimals, preserve 2 non-zero digits
      // to avoid truncation to 0
      // Check if the value would be truncated (not rounded) to 0 with 6 decimals
      // by checking if the value is less than 0.000001 (10^-6)
      if (Math.abs(formattedValue) < 0.000001 && Math.abs(formattedValue) > 0) {
        formattedValue = formatSmallPidValue(formattedValue);
      } else {
        const truncatedTo6 = Number(formattedValue.toFixed(6));
        formattedValue = truncatedTo6;
      }
    }
  }

  const formatted = unitValueToFormattedUnitLabels({
    value: formattedValue,
    units: transformed.units,
  });

  return {
    valueLabel: `${formatted.valueLabel}`,
    unitsLabel: `${formatted.unitsLabel}`,
    valueUnitsLabel: `${formatted.valueUnitsLabel}`,
  };
};
